// *****************************
// *****  Main Navigation  *****
// *****************************

import { debounce } from 'debounce';
import { breakPoint } from './config';
import { Collapse as bootstrapCollapse } from 'bootstrap';
import { keyboardControl } from './keyboard-accessibility-check';

let isMobile = true,
    m = null, // main nav system
    s = null, // header search system
    fm = []; // desktop feature menu system

export const mainNavigation = {
    active: false
}
export const desktopFeatureNavigation = {
    active: false,
    index: 0
}

class AccessibleList {
    constructor(el) {
        this.el = el;
        this.tabIndex = 0;
        this.items = [...this.el.querySelectorAll('a')];
        this.itemsCount = this.items.length;
        this.updateTabIndexes(0);
        // focus behaviour
        for(let x = 0, l = this.items.length; x < l; x++) {
            this.items[x].onfocus = () => {
                if(this.tabIndex != x) {
                    this.tabIndex = x;
                    this.updateTabIndexes();
                }
            };
        }
    }
    updateTabIndexes(setFocus = false) {
        for(let x = 0, l = this.items.length; x < l; x++) {
            this.items[x].setAttribute('tabIndex', x == this.tabIndex ? '0' : '-1');
        }
        if(setFocus) this.items[this.tabIndex].focus();
    }
    nav(mode) {
        if(mode) {
            this.tabIndex+=1;
            if(this.tabIndex >= this.itemsCount) this.tabIndex = 0;
        } else {
            this.tabIndex-=1;
            if(this.tabIndex < 0) this.tabIndex = this.itemsCount - 1;
        }
        this.updateTabIndexes(true);
    }
}

class menu {
    constructor(wrapper, toggleButton) {
        this.wrapper = wrapper;
        this.toggleButton = toggleButton;
        this.expanded = false;
        this.mobileTabElements = [...this.wrapper.querySelectorAll('#full-menu--feature-links li:first-child a, .menu-section > a, .menu-section > button, #full-menu--mobile-search input, #full-menu--social-links li:first-child a, #full-menu--mobile-search button')];
        this.desktopTabElements = [...this.wrapper.querySelectorAll('#full-menu--feature-links li:first-child a, .menu-section:not(.d-lg-none) > a, .menu-section:not(.d-lg-none) .collapse ul li:first-child a, #full-menu--social-links li:first-child a')];
        this.tabElementIndex = -1;
        this.tabElementSubIndex = -1;
        this.sections = [];
        [...this.wrapper.querySelectorAll('.menu-section')].forEach((s) => {
            this.sections.push(new menuSection(s));
        });
        // accessible lists
        this.accessibleLists = [];
        [...this.wrapper.querySelectorAll('.menu-accessible-list')].forEach((l) => {
            this.accessibleLists.push(new AccessibleList(l));
        });
    }
    show() {
        // close headerSearch if necessary
        if(s && s.active) s.hide();
        // close desktopFeatureMenu if necessary
        if(desktopFeatureNavigation.active) {
            fm[desktopFeatureNavigation.index].hide();
        }
        // open menu
        this.expanded = true;
        mainNavigation.active = true;
        this.toggleButton.setAttribute('aria-expanded', true);
        this.wrapper.setAttribute('aria-hidden', false);
        window.requestAnimationFrame(() => {
            document.body.classList.add('menu-active');
            this.tabElementIndex = 0;
            this.wrapper.ontransitionend = (e) => {
                if(e.target == this.wrapper) this.showTransitionEnd();
            };
        });
    }
    showTransitionEnd() {
        // focus on first menu item if keyboard control
        if(keyboardControl.active) {
            if(isMobile) {
                this.mobileTabElements[0].focus();
            } else {
                this.desktopTabElements[0].focus();
            }
        }
        this.wrapper.ontransitionend = null;
    }
    hide(doFocus = false) {
        this.expanded = false;
        mainNavigation.active = false;
        this.tabElementIndex = -1;
        this.toggleButton.setAttribute('aria-expanded', false);
        document.body.classList.remove('menu-active');
        this.wrapper.ontransitionend = (e) => {
            if(e.target == this.wrapper) this.hideTransitionEnd(doFocus);
        };
    }
    hideTransitionEnd(doFocus = false) {
        this.wrapper.setAttribute('aria-hidden', true);
        if(keyboardControl.active && doFocus) this.toggleButton.focus();
        this.wrapper.ontransitionend = null;
    }
}

class menuSection {
    constructor(el) {
        this.wrapper = el;
        this.collapseToggle = el.querySelector('button[data-bs-toggle="collapse"]');
        this.collapseContent = el.querySelector('.collapse');
        this.firstOption = el.querySelector('ul li a');
        // add listeners for expansion to add class to parent wrapper
        if(this.collapseContent != null) {
            this.collapseContent.addEventListener('show.bs.collapse', () => {
                this.wrapper.classList.add('expanded');
            });
            this.collapseContent.addEventListener('shown.bs.collapse', () => {
                if(keyboardControl.active && isMobile) this.firstOption.focus();
            });
            this.collapseContent.addEventListener('hidden.bs.collapse', () => {
                this.wrapper.classList.remove('expanded');
            });
        }
    }
    toMobile() {
        if(this.collapseContent != null) {
            let bsCollapse = bootstrapCollapse.getOrCreateInstance(this.collapseContent);
            bsCollapse.hide();
            this.collapseToggle.removeAttribute('disabled');
        }
    }
    toDesktop() {
        if(this.collapseContent != null) {
            let bsCollapse = bootstrapCollapse.getOrCreateInstance(this.collapseContent);
            bsCollapse.show();
            this.collapseToggle.setAttribute('disabled', 'disabled');
        }
    }
}

const findMenu = document.querySelector('#full-menu');
if(findMenu != null) {

    // create instance of menu

    m = new menu(findMenu, document.querySelector('.header--menu-toggle button'));

    // menu toggle functionality

    m.toggleButton.addEventListener('click', () => {
        if (!m.expanded) {
            // open menu
            m.show();
        } else {
            // close menu
            m.hide();
        }
    });

    // add keyboard accessibility

    const navTabElements = (mode) => {
        let tabElements = isMobile ? m.mobileTabElements : m.desktopTabElements,
            tabElementsLength = tabElements.length,
            lastIndex = tabElementsLength - 1;
        if(mode) {
            // move to next tab element
            m.tabElementIndex = m.tabElementIndex == lastIndex ? 0 : m.tabElementIndex + 1;
        } else {
            // move to previous tab element
            m.tabElementIndex = m.tabElementIndex == 0 ? lastIndex : m.tabElementIndex - 1;
        }
        if(keyboardControl.active) tabElements[m.tabElementIndex].focus();
    };

    // mobile menu
    for(let x = 0, l = m.mobileTabElements.length; x < l; x++) {
        m.mobileTabElements[x].addEventListener('keydown', (e) => {
            if(isMobile) {
                switch (e.key) {
                    // ESCAPE jumps out a level
                    case 'Escape':
                        if(mainNavigation.active) m.hide(true);
                        break;
                    case 'Left':
                    case 'ArrowLeft':
                        e.preventDefault();
                        navTabElements(false);
                        break;
                    case 'Tab':
                    case 'Right':
                    case 'ArrowRight':
                        e.preventDefault();
                        navTabElements(true);
                        break;
                }
            }
        });
    }
    // desktop menu
    for(let x = 0, l = m.desktopTabElements.length; x < l; x++) {
        m.desktopTabElements[x].addEventListener('keydown', (e) => {
            if(!isMobile) {
                switch (e.key) {
                    // ESCAPE jumps out a level
                    case 'Escape':
                        if(!isMobile && mainNavigation.active) m.hide(true);
                        break;
                    case 'Left':
                    case 'ArrowLeft':
                        e.preventDefault();
                        navTabElements(false);
                        break;
                    case 'Tab':
                    case 'Right':
                    case 'ArrowRight':
                        e.preventDefault();
                        navTabElements(true);
                        break;
                }
            }
        });
    }
    // accessible lists
    for(let x = 0, lx = m.accessibleLists.length; x < lx; x++) {
        for(let y = 0, ly = m.accessibleLists[x].items.length; y < ly; y++) {
            m.accessibleLists[x].items[y].addEventListener('keydown', (e) => {
                let isTabElement = y == 0; // look for tabbable element which already has some listeners
                switch (e.key) {
                    // ESCAPE jumps out a level
                    case 'Escape':
                        e.preventDefault();
                        if(isMobile) {
                            m.mobileTabElements[m.tabElementIndex].focus();
                        } else {
                            if(!isTabElement && mainNavigation.active) m.hide(true); // only if not the tabbable element
                        }
                        break;
                    case 'Up':
                    case 'ArrowUp':
                        e.preventDefault();
                        m.accessibleLists[x].nav(false);
                        break;
                    case 'Down':
                    case 'ArrowDown':
                        e.preventDefault();
                        m.accessibleLists[x].nav(true);
                        break;
                    case 'Left':
                    case 'ArrowLeft':
                        e.preventDefault();
                        if(!isTabElement) navTabElements(false); // only if not the tabbable element
                        break;
                    case 'Tab':
                    case 'Right':
                    case 'ArrowRight':
                        e.preventDefault();
                        if(!isTabElement) navTabElements(true); // only if not the tabbable element
                        break;
                }
            });
        }
    }


    // hide or show all menu '.collapse' elements if changing from mobile to desktop or vica versa

    var resize = () => {
        let prev = isMobile;
        isMobile = window.innerWidth < breakPoint.LG;
        if (isMobile && !prev) {
            // collapse all menu sections and enable toggles
            m.sections.forEach((s) => {s.toMobile()});
            if(m.expanded) m.hide(true);
            desktopFeatureNavigation.active = false;
        } else if (!isMobile && prev) {
            // expanded all menu sections and disable toggles
            m.sections.forEach((s) => {s.toDesktop()});
            if(m.expanded) m.hide(true);
        }
    };

    resize();
    window.addEventListener('resize', () => {
        debounce(resize(), 200);
    });

}

// ***************************
// *****  Header Search  *****
// ***************************

export const headerSearch = {
    active: false
}

class HeaderSearchSystem {

    constructor(showBtn, wrapper, closeBtn, searchField) {
        this.showBtn = showBtn;
        this.wrapper = wrapper;
        this.closeBtn = closeBtn;
        this.searchField = searchField;
        this.active = false;
        // remove 'hidden' attribute
        this.wrapper.removeAttribute('hidden');
        // triggers
        this.showBtn.onclick = () => {
            // close menu if necessary
            if(m && m.expanded) m.hide();
            // open search
            if(!this.active) {
                this.show();
            } else if(sideHeaderLayout) {
                this.hide();
            }
        };
        this.closeBtn.onclick = () => {
            if(this.active) this.hide();
        };
        // ESC key closes search
        this.wrapper.addEventListener('keydown', (e) => {
            switch (e.key) {
                // ESCAPE jumps out a level
                case 'Escape':
                    if(this.active) {
                        e.preventDefault();
                        this.hide(true);
                    }
                    break;
            }
        });
        // loop the tabbing if search area is active
        this.closeBtn.addEventListener('keydown', (e) => {
            switch (e.key) {
                // TAB
                case 'Tab':
                    if(this.active) {
                        e.preventDefault();
                        this.searchField.focus();
                    }
                    break;
            }
        });
    }

    show() {
        this.wrapper.ontransitionend = null;
        this.wrapper.setAttribute('aria-hidden', false);
        this.showBtn.setAttribute('aria-expanded', true);
        this.active = true;
        headerSearch.active = true;
        window.requestAnimationFrame(() => {
            this.wrapper.classList.add('search-form--active');
            this.searchField.focus();
        });
    }

    hide(doFocus = false) {
        this.showBtn.setAttribute('aria-expanded', false);
        this.active = false;
        headerSearch.active = false;
        this.wrapper.classList.remove('search-form--active');
        this.wrapper.ontransitionend = (e) => {
            if(e.target == this.wrapper) this.transitionEnd(doFocus);
        };
    }

    transitionEnd(doFocus = false) {
        this.wrapper.setAttribute('aria-hidden', true);
        this.wrapper.ontransitionend = null;
        if(doFocus) this.showBtn.focus();
    }
}

const headerSearchOpenBtn = document.querySelector('#header--desktop-search--open-btn'),
    headerSearchWrapper = document.querySelector('#header--desktop-search--wrapper'),
    headerSearchCloseBtn = document.querySelector('#header--desktop-search--close-btn'),
    headerSearchField = headerSearchWrapper !== null ? headerSearchWrapper.querySelector('input[type="search"]') : null;

if(headerSearchOpenBtn != null && headerSearchWrapper != null && headerSearchCloseBtn != null && headerSearchField != null) {
    s = new HeaderSearchSystem(headerSearchOpenBtn, headerSearchWrapper, headerSearchCloseBtn, headerSearchField);
}

// **********************************
// *****  Desktop feature menu  *****
// **********************************

class DesktopFeatureMenuDropdown {

    constructor(el, index) {
        this.el = el;
        this.index = index;
        this.active = false;
        this.toggle = el.querySelector('.header--feature-menu--dropdown--toggle');
        this.content = el.querySelector('.header--feature-menu--dropdown--content');
        this.focusItems = this.content.querySelectorAll('a, button');
        // toggle behaviour
        this.toggle.addEventListener('click', (e) => {
            if(this.active) {
                // close
                this.hide();
            } else {
                // open
                this.show();
            }
        });
        // keyboard control
        this.el.addEventListener('keydown', (e) => {
            if(this.active) {
                switch (e.key) {
                    // ESCAPE jumps out a level
                    case 'Escape':
                        if(this.active) {
                            e.preventDefault();
                            this.hide(true);
                        }
                        break;
                        case 'Left':
                        case 'ArrowLeft':
                        case 'Up':
                        case 'ArrowUp':
                            e.preventDefault();
                            this.navFocusItems(false);
                            break;
                        case 'Tab':
                        case 'Right':
                        case 'ArrowRight':
                        case 'Down':
                        case 'ArrowDown':
                            e.preventDefault();
                            this.navFocusItems(true);
                            break;
                }
            }
        });
        // watch for clicks elsewhere and close if necessary
        let $this = this;
        document.body.addEventListener('click', (e) => {
            $this.checkClickOutside(e.target);
        });
        // hide on header hide
        window.addEventListener('headerHide', () => {
            if($this.active) $this.hide();
        });

    }

    checkClickOutside(t) {
        if (this.active) {
            let inMenu = false;
            while (t != document.body) {
                if (t == this.el) {
                    inMenu = true;
                    break
                }
                t = t.parentNode;
            }
            if (!inMenu) {
                this.hide();
            }
        }
    }

    show() {
        if(mainNavigation.active) m.hide();
        this.toggle.setAttribute('aria-expanded',true);
        this.content.setAttribute('aria-hidden',false);
        this.active = true;
        desktopFeatureNavigation.active = true;
        desktopFeatureNavigation.index = this.index;
        window.requestAnimationFrame(() => {
            this.content.classList.add('show');
            this.content.ontransitionend = ((e) => {
                if(e.target == this.content) {
                    this.content.ontransitionend = null;
                    // set focus?
                    this.focusIndex = 0;
                    if(keyboardControl.active) {
                        this.focusItems[0].focus();
                    }
                }
            });
        });
    }

    hide(doFocus = false) {
        this.toggle.setAttribute('aria-expanded',false);
        this.active = false;
        desktopFeatureNavigation.active = false;
        this.content.classList.remove('show');
        this.content.ontransitionend = ((e) => {
            if(e.target == this.content) {
                this.content.setAttribute('aria-hidden',true);
                this.active = false;
                this.content.ontransitionend = null;
                if(doFocus) this.toggle.focus();
            }
        });
    }

    navFocusItems(mode) {
        if(mode) {
            this.focusIndex += 1;
            if(this.focusIndex >= this.focusItems.length) this.focusIndex = 0;
        } else {
            this.focusIndex -= 1;
            if(this.focusIndex < 0) this.focusIndex = this.focusItems.length - 1;
        }
        this.focusItems[this.focusIndex].focus();
    }

}

let dd = document.querySelectorAll('.header--feature-menu .header--feature-menu--dropdown');
if(dd != null) {
    [...dd].forEach((d, index) => {
        fm.push(new DesktopFeatureMenuDropdown(d, index));
    });
};

